/**
 * LandingPage Component
 *
 * Main marketing landing page for PurchasePuffin.
 * Sections: Hero, Problem, Solution, Features, How It Works, Stats, Testimonials, FAQ, CTA.
 */

import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getBrand } from '@puffin-core/brand-assets';
import {
  MarketingSeo,
  CTASection,
  useAnalytics,
  organization,
  softwareApplication,
  webPage,
  faqPage,
} from '@puffin-core/marketing-shell';

import {
  PURCHASE_MARKETING_ANCHORS,
  PURCHASE_MARKETING_ROUTES,
} from '../constants/purchaseMarketingRoutePaths';

const APP_KEY = 'purchase';
const LANDING_TITLE = 'PurchasePuffin - Custom E-Commerce Platform for Personalized Products';
const LANDING_DESCRIPTION =
  'Build custom storefronts with multi-tenant e-commerce, white-label partner portals, and dynamic pricing.';
const LANDING_CANONICAL = 'https://purchasepuffin.com/';

/**
 * One stat card inside the decorative hero mockup.
 *
 * Extracted from `<HeroMockup>` to cut its JSX nesting depth
 * (`web/render-method-complexity`): the mockup's deepest branch ran
 * div.mockup → div.mockup-body → div.mockup-content → div.mockup-card-grid →
 * div.mockup-card → div.mockup-card-value. Same markup, same classNames —
 * the DOM is byte-identical.
 *
 * @param {Object} props
 * @param {string} props.value - Decorative metric value (mockup copy).
 * @param {string} props.label - Translated card label.
 * @returns {React.JSX.Element} One mockup stat card
 */
function MockupCard({ value, label }) {
  return (
    <div className="mockup-card">
      <div className="mockup-card-value">{value}</div>
      <div className="mockup-card-label">{label}</div>
    </div>
  );
}

MockupCard.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

/**
 * Dashboard mockup visual for the hero section.
 * Decorative element hidden from assistive technology — but the visible
 * sidebar/card labels are still user-facing copy and live in the i18n
 * bundle (MES-001) so EN/ES parity holds.
 *
 * @param {Object} props
 * @param {Function} props.t - react-i18next translator
 * @returns {React.JSX.Element} Decorative dashboard mockup
 */
function HeroMockup({ t }) {
  return (
    <div className="mockup" aria-hidden="true">
      <div className="mockup-header">
        <span className="mockup-dot mockup-dot-red" />
        <span className="mockup-dot mockup-dot-yellow" />
        <span className="mockup-dot mockup-dot-green" />
      </div>
      <div className="mockup-body">
        <div className="mockup-sidebar">
          <div className="mockup-sidebar-item mockup-sidebar-item-active">
            {t('landing.hero.mockup.sidebar.dashboard')}
          </div>
          <div className="mockup-sidebar-item">{t('landing.hero.mockup.sidebar.products')}</div>
          <div className="mockup-sidebar-item">{t('landing.hero.mockup.sidebar.orders')}</div>
          <div className="mockup-sidebar-item">{t('landing.hero.mockup.sidebar.partners')}</div>
        </div>
        <div className="mockup-content">
          <p className="mockup-content-title">{t('landing.hero.mockup.contentTitle')}</p>
          <div className="mockup-card-grid">
            <MockupCard value="156" label={t('landing.hero.mockup.cards.products')} />
            <MockupCard value="42" label={t('landing.hero.mockup.cards.ordersToday')} />
            <MockupCard value="$8,340" label={t('landing.hero.mockup.cards.revenue')} />
            <MockupCard value="8" label={t('landing.hero.mockup.cards.partners')} />
          </div>
        </div>
      </div>
    </div>
  );
}

HeroMockup.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * The hero call-to-action pair.
 *
 * Extracted from `<LandingPage>` to cut the page's JSX nesting depth
 * (`web/render-method-complexity`): the hero branch ran fragment → section.hero
 * → div.container → div.hero-grid → div.hero-content → div.hero-actions → Link.
 * Same wrapper, same classNames, same link order — the DOM is byte-identical,
 * and the page keeps its `<h1>` (which `web/require-heading-hierarchy` needs).
 *
 * @param {Object} props
 * @param {Function} props.t - react-i18next translator
 * @returns {React.JSX.Element} Hero CTA button row
 */
function HeroActions({ t }) {
  return (
    <div className="hero-actions">
      <Link to={PURCHASE_MARKETING_ROUTES.CONTACT} className="btn-primary btn-lg">
        {t('landing.hero.ctaPrimary')}
      </Link>
      <a href={`#${PURCHASE_MARKETING_ANCHORS.HOW_IT_WORKS}`} className="btn-secondary btn-lg">
        {t('landing.hero.ctaSecondary')}
      </a>
    </div>
  );
}

HeroActions.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * Main marketing landing page for PurchasePuffin.
 * Renders hero, problem, solution, features, how-it-works, stats,
 * testimonials, FAQ, and CTA sections with SEO metadata and schema markup.
 *
 * @returns {React.JSX.Element} Full marketing landing page with all sections
 */
function LandingPage() {
  const { t } = useTranslation();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const { trackPageView } = useAnalytics();
  const brand = getBrand(APP_KEY);

  useEffect(() => {
    trackPageView('/', 'Landing Page');
  }, [trackPageView]);

  const problems = t('landing.problems.items', { returnObjects: true });
  const solutions = t('landing.solutions.items', { returnObjects: true });
  const features = t('landing.features.items', { returnObjects: true });
  const steps = t('landing.steps.items', { returnObjects: true });
  const stats = t('landing.stats.items', { returnObjects: true });
  const testimonials = t('landing.testimonials.items', { returnObjects: true });
  const faqs = t('landing.faq.items', { returnObjects: true });

  const toggleFaq = (id) => {
    setExpandedFaq((prev) => (prev === id ? null : id));
  };

  const jsonLd = useMemo(() => {
    const faqList = Array.isArray(faqs) ? faqs : [];
    const schemas = [
      organization({
        appKey: APP_KEY,
        brand,
        description: 'Custom e-commerce platform for product storefronts',
      }),
      softwareApplication({ appKey: APP_KEY, brand }),
      webPage({
        appKey: APP_KEY,
        brand,
        url: LANDING_CANONICAL,
        title: LANDING_TITLE,
        description: LANDING_DESCRIPTION,
      }),
    ];
    if (faqList.length > 0) {
      schemas.push(
        faqPage({
          items: faqList.map((f) => ({ question: f.question, answer: f.answer })),
          t,
        }),
      );
    }
    return schemas;
  }, [brand, faqs, t]);

  const ogImageUrl = `https://purchasepuffin.com${brand.assets.ogCard}`;

  return (
    <>
      <MarketingSeo
        title={LANDING_TITLE}
        description={LANDING_DESCRIPTION}
        canonical={LANDING_CANONICAL}
        ogType="website"
        ogImage={ogImageUrl}
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="hero" aria-labelledby="hero-title">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <span className="hero-badge">{t('landing.hero.badge')}</span>
              <h1 id="hero-title" className="hero-title">
                {t('landing.hero.title')}
              </h1>
              <p className="hero-subtitle">{t('landing.hero.subtitle')}</p>
              <HeroActions t={t} />
            </div>
            <div className="hero-visual">
              <HeroMockup t={t} />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section-marketing" aria-labelledby="problem-heading">
        <div className="container">
          <h2 id="problem-heading" className="section-heading">
            {t('landing.problems.heading')}
          </h2>
          <p className="section-description">{t('landing.problems.description')}</p>
          <div className="problem-grid">
            {problems.map((problem) => (
              <div key={problem.id} className="problem-card">
                <div className="problem-card-icon" aria-hidden="true">
                  {problem.icon}
                </div>
                <h3 className="problem-card-title">{problem.title}</h3>
                <p className="problem-card-description">{problem.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="section-marketing section-light" aria-labelledby="solution-heading">
        <div className="container">
          <h2 id="solution-heading" className="section-heading">
            {t('landing.solutions.heading')}
          </h2>
          <p className="section-description">{t('landing.solutions.description')}</p>
          <div className="solution-grid">
            {solutions.map((solution) => (
              <div key={solution.id} className="solution-card">
                <div className="solution-card-icon" aria-hidden="true">
                  {solution.icon}
                </div>
                <h3 className="solution-card-title">{solution.title}</h3>
                <p className="solution-card-description">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id={PURCHASE_MARKETING_ANCHORS.FEATURES}
        className="section-marketing"
        aria-labelledby="features-heading"
      >
        <div className="container">
          <h2 id="features-heading" className="section-heading">
            {t('landing.features.heading')}
          </h2>
          <p className="section-description">{t('landing.features.description')}</p>
          <div className="feature-grid">
            {features.map((feature) => (
              <div key={feature.id} className="feature-card">
                <div className="feature-card-icon" aria-hidden="true">
                  {feature.icon}
                </div>
                <h3 className="feature-card-title">{feature.title}</h3>
                <p className="feature-card-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section
        id={PURCHASE_MARKETING_ANCHORS.HOW_IT_WORKS}
        className="section-marketing section-light"
        aria-labelledby="how-it-works-heading"
      >
        <div className="container">
          <h2 id="how-it-works-heading" className="section-heading">
            {t('landing.steps.heading')}
          </h2>
          <p className="section-description">{t('landing.steps.description')}</p>
          <div className="steps-grid">
            {steps.map((step) => (
              <div key={step.id} className="step-card">
                <div className="step-number" aria-hidden="true">
                  {step.number}
                </div>
                <h3 className="step-card-title">{step.title}</h3>
                <p className="step-card-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-marketing section-dark" aria-labelledby="stats-heading">
        <div className="container">
          <h2 id="stats-heading" className="section-heading">
            {t('landing.stats.heading')}
          </h2>
          <p className="section-description">{t('landing.stats.description')}</p>
          <div className="stats-grid">
            {stats.map((stat) => (
              <div key={stat.id}>
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-marketing" aria-labelledby="testimonials-heading">
        <div className="container">
          <h2 id="testimonials-heading" className="section-heading">
            {t('landing.testimonials.heading')}
          </h2>
          <p className="section-description">{t('landing.testimonials.description')}</p>
          <div className="testimonial-grid">
            {testimonials.map((testimonial) => (
              <blockquote key={testimonial.id} className="testimonial-card">
                <p className="testimonial-quote">{testimonial.quote}</p>
                <div className="testimonial-attribution">
                  <cite className="testimonial-name">{testimonial.name}</cite>
                  <span className="testimonial-role">
                    {testimonial.role}, {testimonial.company}
                  </span>
                </div>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-marketing section-light" aria-labelledby="faq-heading">
        <div className="container">
          <h2 id="faq-heading" className="section-heading">
            {t('landing.faq.heading')}
          </h2>
          <p className="section-description">{t('landing.faq.description')}</p>
          <div className="faq-list">
            {faqs.map((faq) => (
              <div key={faq.id} className="faq-item">
                <button
                  type="button"
                  className={`faq-question${expandedFaq === faq.id ? ' faq-question-active' : ''}`}
                  onClick={() => toggleFaq(faq.id)}
                  aria-expanded={expandedFaq === faq.id}
                  aria-controls={`faq-answer-${faq.id}`}
                >
                  <span>{faq.question}</span>
                  <span className="faq-toggle" aria-hidden="true">
                    {expandedFaq === faq.id ? '\u2212' : '+'}
                  </span>
                </button>
                {expandedFaq === faq.id && (
                  <div id={`faq-answer-${faq.id}`} className="faq-answer" role="region">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        heading={t('landing.cta.heading')}
        subheading={t('landing.cta.description')}
        primaryAction={t('landing.cta.primaryAction')}
        primaryLink={PURCHASE_MARKETING_ROUTES.CONTACT}
        secondaryAction={t('landing.cta.secondaryAction')}
        secondaryLink={`#${PURCHASE_MARKETING_ANCHORS.FEATURES}`}
      />
    </>
  );
}

export default LandingPage;
